/* MPL pickup-point picker for the cart (Hound Theory).
   One component, theme-agnostic: needs only /cart/*.js and (for the drawer) Dawn's <cart-drawer>.renderContents.
   State lives in the cart: the marker line (SKU MPL-PICKUP) + its properties are the source of truth. */
(() => {
  if (customElements.get('mpl-picker')) return;

  const I18N = {
    hu: {
      title: 'Szállítási mód', home: 'Házhoz szállítás (MPL)', pickup: 'Csomagautomata / PostaPont / Posta',
      change: 'Módosítás', choose: 'Válassz átvételi pontot', need: 'A pénztárhoz előbb válassz átvételi pontot.',
      sheetTitle: 'Átvételi pont kiválasztása', search: 'Irányítószám, település vagy cím',
      locate: 'A helyzetem', list: 'Lista', map: 'Térkép', close: 'Bezárás', select: 'Kiválasztom',
      CS: 'Csomagautomata', PP: 'PostaPont', PM: 'Posta', today: 'Ma', closed: 'zárva', allDay: '0–24',
      loading: 'Átvételi pontok betöltése…', none: 'Nincs találat. Próbálj irányítószámot vagy települést.',
      saving: 'Mentés…', saved: 'Átvételi pont elmentve.', error: 'Hiba történt, próbáld újra.',
      locErr: 'A helyzeted nem érhető el.', typeHint: 'Kezdd el beírni az irányítószámot vagy a települést.',
      km: 'km'
    },
    en: {
      title: 'Delivery method', home: 'Home delivery (MPL)', pickup: 'Parcel locker / PostaPont / Post office',
      change: 'Change', choose: 'Choose a pickup point', need: 'Please choose a pickup point before checkout.',
      sheetTitle: 'Choose a pickup point', search: 'Postcode, town or address',
      locate: 'My location', list: 'List', map: 'Map', close: 'Close', select: 'Select',
      CS: 'Parcel locker', PP: 'PostaPont', PM: 'Post office', today: 'Today', closed: 'closed', allDay: '24/7',
      loading: 'Loading pickup points…', none: 'No results. Try a postcode or town.',
      saving: 'Saving…', saved: 'Pickup point saved.', error: 'Something went wrong, please try again.',
      locErr: 'Your location is not available.', typeHint: 'Start typing a postcode or town.',
      km: 'km'
    }
  };
  const CDN = 'https://cdnjs.cloudflare.com/ajax/libs/';
  const LEAFLET = { css: CDN + 'leaflet/1.9.4/leaflet.min.css', js: CDN + 'leaflet/1.9.4/leaflet.min.js' };
  const CLUSTER = {
    css: [CDN + 'leaflet.markercluster/1.5.3/MarkerCluster.min.css', CDN + 'leaflet.markercluster/1.5.3/MarkerCluster.Default.min.css'],
    js: CDN + 'leaflet.markercluster/1.5.3/leaflet.markercluster.min.js'
  };
  const TYPE_COLOR = { CS: '#0284c7', PP: '#ea580c', PM: '#16a34a' };   /* locker blue, PostaPont orange, post office green */

  const norm = (s) => (s || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  const esc = (s) => (s || '').toString().replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const km = (a, b) => {
    const r = Math.PI / 180, dLat = (b[0] - a[0]) * r, dLon = (b[1] - a[1]) * r;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * r) * Math.cos(b[0] * r) * Math.sin(dLon / 2) ** 2;
    return 12742 * Math.asin(Math.sqrt(h));
  };
  const loadCss = (href) => new Promise((res) => {
    if (document.querySelector(`link[href="${href}"]`)) return res();
    const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = href; l.onload = res; l.onerror = res; document.head.append(l);
  });
  const loadJs = (src) => new Promise((res, rej) => {
    if (document.querySelector(`script[src="${src}"]`)) return res();
    const s = document.createElement('script'); s.src = src; s.onload = res; s.onerror = rej; document.head.append(s);
  });

  let pointsPromise = null;
  let zipTable = {};   /* postcode -> [lat, lon] (real locations; MPL's own point postcodes can be service codes) */
  function loadPoints(url) {
    if (!pointsPromise) {
      pointsPromise = fetch(url).then((r) => r.json()).then((doc) => {
        zipTable = doc.zips || {};
        return doc.points.map((p) => ({
          id: p[0], type: p[1], name: p[2], zip: p[3], city: p[4], street: p[5], lat: p[6], lon: p[7], hours: p[8],
          address: p[9] || `${p[3]} ${p[4]}, ${p[5]}`,
          key: norm(`${p[9] || ''} ${p[3]} ${p[4]} ${p[5]} ${p[2]}`)
        }));
      });
    }
    return pointsPromise;
  }

  async function cartJson(path, body) {
    const r = await fetch(`${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}${path}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body)
    });
    if (!r.ok) throw new Error(`${path} ${r.status}`);
    return r.json();
  }

  class MplPicker extends HTMLElement {
    connectedCallback() {
      this.t = I18N[(this.dataset.lang || 'hu').slice(0, 2)] || I18N.hu;
      if (this.dataset.cleanup) { this.cleanup(); return; }
      this.querySelectorAll('[data-i18n]').forEach((el) => { const k = el.dataset.i18n; if (this.t[k]) el.textContent = this.t[k]; });
      this.marker = Number(this.dataset.markerVariant);
      this.hasPoint = !!this.dataset.pointId;
      this.querySelectorAll('input[type=radio]').forEach((r) => r.addEventListener('change', () => this.onMode(r.value)));
      this.querySelector('.mpl-picker__open').addEventListener('click', () => this.openSheet());
      this.querySelector('.mpl-picker__change').addEventListener('click', () => this.openSheet());
      this.repair();
      this.sync();
    }

    disconnectedCallback() { if (!this.dataset.cleanup) this.toggleCheckout(true); }

    async cleanup() {
      try { await cartJson('cart/change.js', { id: this.dataset.markerKey, quantity: 0 }); await this.refresh(); } catch (e) { /* ignore */ }
    }

    get mode() { const r = this.querySelector('input[type=radio]:checked'); return r ? r.value : 'home'; }

    /* checkout is allowed unless pickup is selected without a point */
    sync() {
      const pickup = this.mode === 'pickup';
      this.querySelector('.mpl-picker__chosen').hidden = !(pickup && this.hasPoint);
      this.querySelector('.mpl-picker__need').hidden = !(pickup && !this.hasPoint);
      this.toggleCheckout(!(pickup && !this.hasPoint));
    }

    toggleCheckout(enabled) {
      const scope = this.closest('cart-drawer, #CartDrawer, .cart__footer, form, body') || document;
      const root = this.dataset.context === 'drawer' ? (document.querySelector('cart-drawer') || document) : document;
      root.querySelectorAll('[name="checkout"], #CartDrawer-Checkout, #checkout').forEach((b) => {
        if (enabled) { if (b.dataset.mplDisabled) { b.disabled = false; delete b.dataset.mplDisabled; } }
        else if (!b.disabled) { b.disabled = true; b.dataset.mplDisabled = '1'; }
      });
      root.querySelectorAll('.additional-checkout-buttons, .cart__dynamic-checkout-buttons').forEach((el) => {
        el.style.display = enabled ? '' : 'none';
      });
      void scope;
    }

    status(msg) { const s = this.querySelector('.mpl-picker__status'); if (s) s.textContent = msg || ''; }

    /* keep cart consistent: one marker, quantity 1 */
    async repair() {
      const qty = Number(this.dataset.markerQty || 0);
      if (qty > 1 && this.dataset.markerKey) {
        try { await cartJson('cart/change.js', { id: this.dataset.markerKey, quantity: 1 }); this.refresh(); } catch (e) { /* ignore */ }
      }
    }

    async onMode(mode) {
      if (mode === 'home') {
        if (this.dataset.markerKey) await this.removeMarker();
        this.hasPoint = false;
        this.sync();
      } else {
        this.sync();
        if (!this.hasPoint) this.openSheet();
      }
    }

    async removeMarker() {
      this.status(this.t.saving);
      try {
        await cartJson('cart/change.js', { id: this.dataset.markerKey, quantity: 0 });
        await cartJson('cart/update.js', { attributes: { mpl_mode: 'home', mpl_point_id: '', mpl_point_name: '', mpl_point_type: '', mpl_point_zip: '', mpl_point_city: '' } });
        await this.refresh();
      } catch (e) { this.status(this.t.error); }
    }

    async choose(p) {
      this.status(this.t.saving);
      const address = p.address;
      try {
        if (this.dataset.markerKey) await cartJson('cart/change.js', { id: this.dataset.markerKey, quantity: 0 });
        await cartJson('cart/add.js', {
          items: [{
            id: this.marker, quantity: 1,
            properties: {
              'Átvételi pont': `${p.name} – ${address}`,
              _mpl_id: p.id, _mpl_type: p.type, _mpl_name: p.name, _mpl_zip: p.zip, _mpl_city: p.city, _mpl_address: address
            }
          }]
        });
        await cartJson('cart/update.js', {
          attributes: { mpl_mode: p.type, mpl_point_id: p.id, mpl_point_name: p.name, mpl_point_type: p.type, mpl_point_zip: p.zip, mpl_point_city: p.city }
        });
        this.hasPoint = true;
        Sheet.close();
        this.status(this.t.saved);
        await this.refresh();
      } catch (e) { this.status(this.t.error); }
    }

    /* re-render the cart UI from the server so totals, lines and this block stay in sync */
    async refresh() {
      const drawer = document.querySelector('cart-drawer');
      if (this.dataset.context === 'drawer' && drawer && typeof drawer.renderContents === 'function') {
        const r = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`);
        const sections = await r.json();
        drawer.renderContents({ id: null, sections });
      } else {
        window.location.reload();
      }
    }

    openSheet() { Sheet.open(this); }
  }

  /* one shared full-screen sheet with list + map */
  const Sheet = {
    el: null, owner: null, map: null, layer: null, origin: null, points: [],
    build(t) {
      const d = document.createElement('div');
      d.className = 'mpl-sheet';
      d.setAttribute('role', 'dialog'); d.setAttribute('aria-modal', 'true'); d.setAttribute('aria-label', t.sheetTitle);
      d.innerHTML = `
        <div class="mpl-sheet__panel">
          <header class="mpl-sheet__head">
            <h2 class="mpl-sheet__title">${esc(t.sheetTitle)}</h2>
            <button type="button" class="mpl-sheet__close" aria-label="${esc(t.close)}">×</button>
          </header>
          <div class="mpl-sheet__controls">
            <input type="search" class="mpl-sheet__search" placeholder="${esc(t.search)}" autocomplete="postal-code" inputmode="search">
            <button type="button" class="mpl-sheet__locate">📍 ${esc(t.locate)}</button>
          </div>
          <div class="mpl-sheet__filters">
            ${['CS', 'PP', 'PM'].map((k) => `<label class="mpl-chip mpl-chip--${k}"><input type="checkbox" value="${k}" checked><span>${esc(t[k])}</span></label>`).join('')}
          </div>
          <div class="mpl-sheet__tabs" role="tablist">
            <button type="button" role="tab" class="mpl-tab is-active" data-tab="list">${esc(t.list)}</button>
            <button type="button" role="tab" class="mpl-tab" data-tab="map">${esc(t.map)}</button>
          </div>
          <div class="mpl-sheet__body">
            <ul class="mpl-sheet__list" data-pane="list"></ul>
            <div class="mpl-sheet__map" data-pane="map" hidden></div>
          </div>
        </div>`;
      document.body.append(d);
      d.addEventListener('click', (e) => { if (e.target === d) this.close(); });
      d.querySelector('.mpl-sheet__close').addEventListener('click', () => this.close());
      d.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      const search = d.querySelector('.mpl-sheet__search');
      let tm; search.addEventListener('input', () => { clearTimeout(tm); tm = setTimeout(() => { this.origin = null; this.render(); }, 180); });
      d.querySelector('.mpl-sheet__locate').addEventListener('click', () => this.locate());
      d.querySelectorAll('.mpl-chip input').forEach((c) => c.addEventListener('change', () => this.render()));
      d.querySelectorAll('.mpl-tab').forEach((b) => b.addEventListener('click', () => this.tab(b.dataset.tab)));
      d.querySelector('.mpl-sheet__list').addEventListener('click', (e) => {
        const b = e.target.closest('[data-pick]'); if (b) this.pick(b.dataset.pick);
      });
      this.el = d;
    },
    async open(owner) {
      this.owner = owner; this.t = owner.t;
      if (!this.el) this.build(this.t);
      this.el.classList.add('is-open'); document.documentElement.classList.add('mpl-sheet-open');
      const list = this.el.querySelector('.mpl-sheet__list');
      if (!this.points.length) {
        list.innerHTML = `<li class="mpl-sheet__msg">${esc(this.t.loading)}</li>`;
        this.points = await loadPoints(owner.dataset.pointsUrl);
      }
      this.render();
      setTimeout(() => this.el.querySelector('.mpl-sheet__search').focus(), 50);
    },
    close() {
      if (!this.el) return;
      this.el.classList.remove('is-open'); document.documentElement.classList.remove('mpl-sheet-open');
      if (this.owner && !this.owner.hasPoint) {
        /* closed without choosing: fall back to home delivery */
        const home = this.owner.querySelector('input[value=home]'); if (home) home.checked = true;
        this.owner.sync();
      }
    },
    types() { return [...this.el.querySelectorAll('.mpl-chip input:checked')].map((c) => c.value); },
    results() {
      const q = norm(this.el.querySelector('.mpl-sheet__search').value);
      const types = this.types();
      let pts = this.points.filter((p) => types.includes(p.type));
      let origin = this.origin;
      if (q) {
        if (/^\d{1,4}$/.test(q)) {
          /* postcode -> real location (exact, else centre of the matching postcodes, else shorter prefix), then nearest points */
          if (!origin) {
            if (zipTable[q]) origin = zipTable[q];
            else {
              for (let k = q.length; k >= 1 && !origin; k--) {
                const m = Object.keys(zipTable).filter((z) => z.startsWith(q.slice(0, k))).map((z) => zipTable[z]);
                if (m.length) origin = [m.reduce((a, c) => a + c[0], 0) / m.length, m.reduce((a, c) => a + c[1], 0) / m.length];
              }
            }
          }
          if (!origin) return [];
        } else {
          const words = q.split(/\s+/);
          pts = pts.filter((p) => words.every((w) => p.key.includes(w)));
        }
      } else if (!origin) {
        return [];
      }
      const exact = /^\d{4}$/.test(q) ? q : null;   /* points at exactly that postcode first, then by distance */
      if (origin) pts = pts.map((p) => ({ ...p, d: km(origin, [p.lat, p.lon]) }))
        .sort((a, b) => ((exact && b.zip === exact) - (exact && a.zip === exact)) || a.d - b.d);
      return pts.slice(0, 30);
    },
    hoursToday(p) {
      const h = Array.isArray(p.hours) ? p.hours[(new Date().getDay() + 6) % 7] : p.hours;
      if (!h) return this.t.closed;
      return h === '0-24' ? this.t.allDay : h.replace(/:00/g, '').replace(/-/g, '–');
    },
    render() {
      const list = this.el.querySelector('.mpl-sheet__list');
      const q = this.el.querySelector('.mpl-sheet__search').value.trim();
      const res = this.results();
      this.current = res;
      if (!q && !this.origin) list.innerHTML = `<li class="mpl-sheet__msg">${esc(this.t.typeHint)}</li>`;
      else if (!res.length) list.innerHTML = `<li class="mpl-sheet__msg">${esc(this.t.none)}</li>`;
      else list.innerHTML = res.map((p) => `
        <li class="mpl-point mpl-point--${p.type}">
          <span class="mpl-point__type mpl-point__type--${p.type}">${esc(this.t[p.type])}</span>
          <span class="mpl-point__name">${esc(p.name)}</span>
          <span class="mpl-point__addr">${esc(p.address)}${p.d != null ? ` · ${p.d.toFixed(1)} ${this.t.km}` : ''}</span>
          <span class="mpl-point__hours">${esc(this.t.today)}: ${esc(this.hoursToday(p))}</span>
          <button type="button" class="mpl-point__pick button button--secondary" data-pick="${esc(p.id)}">${esc(this.t.select)}</button>
        </li>`).join('');
      if (this.map) this.drawMap();
    },
    pick(id) {
      const p = this.points.find((x) => x.id === id);
      if (p && this.owner) this.owner.choose(p);
    },
    locate() {
      if (!navigator.geolocation) return this.msg(this.t.locErr);
      navigator.geolocation.getCurrentPosition((pos) => {
        this.origin = [pos.coords.latitude, pos.coords.longitude];
        this.el.querySelector('.mpl-sheet__search').value = '';
        this.render();
        if (this.map) this.map.setView(this.origin, 13);
      }, () => this.msg(this.t.locErr), { timeout: 10000, maximumAge: 600000 });
    },
    msg(m) { this.el.querySelector('.mpl-sheet__list').innerHTML = `<li class="mpl-sheet__msg">${esc(m)}</li>`; },
    async tab(name) {
      this.el.querySelectorAll('.mpl-tab').forEach((b) => b.classList.toggle('is-active', b.dataset.tab === name));
      this.el.querySelector('[data-pane=list]').hidden = name !== 'list';
      this.el.querySelector('[data-pane=map]').hidden = name !== 'map';
      if (name === 'map') await this.ensureMap();
    },
    async ensureMap() {
      if (!this.map) {
        await Promise.all([loadCss(LEAFLET.css), ...CLUSTER.css.map(loadCss)]);
        await loadJs(LEAFLET.js); await loadJs(CLUSTER.js);
        const L = window.L;
        this.map = L.map(this.el.querySelector('.mpl-sheet__map'), { zoomControl: true }).setView([47.16, 19.5], 7);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19, attribution: '&copy; OpenStreetMap'
        }).addTo(this.map);
        this.map.on('popupopen', (e) => {
          const b = e.popup.getElement().querySelector('[data-pick]'); if (b) b.addEventListener('click', () => this.pick(b.dataset.pick));
        });
      }
      setTimeout(() => this.map.invalidateSize(), 50);
      this.drawMap();
    },
    drawMap() {
      const L = window.L; if (!L || !this.map) return;
      const css = getComputedStyle(document.documentElement);
      this.colors = Object.fromEntries(Object.keys(TYPE_COLOR).map((k) => [k, css.getPropertyValue('--mpl-color-' + k).trim() || TYPE_COLOR[k]]));
      if (this.layer) this.map.removeLayer(this.layer);
      const types = this.types();
      const pts = this.points.filter((p) => types.includes(p.type));
      this.layer = L.markerClusterGroup({ showCoverageOnHover: false, maxClusterRadius: 45 });
      pts.forEach((p) => {
        const m = L.circleMarker([p.lat, p.lon], { radius: 8, weight: 2, color: '#fff', fillColor: this.colors[p.type], fillOpacity: 1 });
        m.bindPopup(`<strong>${esc(p.name)}</strong><br>${esc(p.address)}<br>${esc(this.t[p.type])} · ${esc(this.t.today)}: ${esc(this.hoursToday(p))}<br><button type="button" class="mpl-point__pick button" data-pick="${esc(p.id)}">${esc(this.t.select)}</button>`);
        this.layer.addLayer(m);
      });
      this.map.addLayer(this.layer);
      const res = this.current || [];
      if (this.origin) this.map.setView(this.origin, 13);
      else if (res.length) this.map.fitBounds(L.latLngBounds(res.slice(0, 10).map((p) => [p.lat, p.lon])).pad(0.3), { maxZoom: 14 });
    }
  };

  customElements.define('mpl-picker', MplPicker);
})();
